import React, { useState } from 'react';
import { SafeRender } from '../../../utils/formattingUtils';
import { updateUserStatus } from '../../../services/firebase';
import { toast } from 'react-hot-toast';
import { useAuthContext } from '../../../hooks/useAuthContext';

// Helper function to get profile picture with fallback
const getProfileImage = (user) => {
  return user.profilePicture || user.basicInfo?.photoURL || user.photoURL || null;
};

// Helper function to get initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Helper function to get job search status display
const getJobSearchStatusDisplay = (user) => {
  const status = user.preferences?.jobSearchStatus || user.jobSearchStatus || 'not-interested';
  
  const statusMap = {
    'actively-looking': { label: 'Actively Looking', color: 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-400/20' },
    'open-to-opportunities': { label: 'Open to Opportunities', color: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-400/20' },
    'not-interested': { label: 'Not Interested', color: 'bg-gray-50 text-gray-600 ring-gray-600/20 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-400/20' }
  };
  
  return statusMap[status] || statusMap['not-interested'];
};

export default function UserProfileTab({ user }) {
  const { user: currentUser } = useAuthContext();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(user?.status || 'active');

  if (!user) return null;

  const handleStatusChange = async (newStatus) => {
    if (!currentUser?.email) {
      toast.error('You must be logged in to update user status');
      return;
    }

    setIsUpdatingStatus(true);
    try {
      const result = await updateUserStatus(user.id, newStatus, currentUser.email);
      
      if (result.success) {
        setCurrentStatus(newStatus);
        toast.success(`User status updated to ${newStatus}`);
      } else {
        toast.error(`Failed to update status: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const profilePic = getProfileImage(user);
  const initials = getInitials(user.displayName || user.email);
  const jobSearchStatus = getJobSearchStatusDisplay(user);
  const userStatus = user.status || 'active';
  const statusConfig = {
    'waitlist': { label: 'Waitlist', color: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-400/20' },
    'invited': { label: 'Invited', color: 'bg-blue-50 text-blue-800 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-400/20' },
    'active': { label: 'Active', color: 'bg-green-50 text-green-800 ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-400/20' }
  };
  const statusBadge = statusConfig[userStatus] || statusConfig['active'];

  return (
    <div className="space-y-6">
      {/* Profile Picture and Status Indicators */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col items-center">
            {/* Profile Picture */}
            {profilePic ? (
              <img 
                alt={user.displayName} 
                src={profilePic} 
                className="size-24 rounded-full bg-gray-50 dark:bg-gray-800 object-cover ring-4 ring-white dark:ring-gray-700"
              />
            ) : (
              <div className="size-24 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center ring-4 ring-white dark:ring-gray-700">
                <span className="text-2xl font-medium text-gray-600 dark:text-gray-300">
                  {initials}
                </span>
              </div>
            )}
            
            {/* Name */}
            <h3 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">
              {user.displayName}
            </h3>
            
            {/* Current Role */}
            {(user.basicInfo?.currentRole || user.currentRole) && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 text-center">
                {user.basicInfo?.currentRole || user.currentRole}
              </p>
            )}
            
            {/* Status Badges */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${statusBadge.color}`}>
                {statusBadge.label}
              </span>
              <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${jobSearchStatus.color}`}>
                {jobSearchStatus.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Summary - Moved to top */}
      {user.profileSummary && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">Profile Summary</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{user.profileSummary}</p>

            {/* Key skills */}
            {user.keySkills && user.keySkills.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Key Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {user.keySkills.map((skill, index) => (
                    <span key={index} className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20 dark:ring-green-400/20">
                      {typeof skill === 'string' ? skill : skill.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Pain Points */}
            {user.painPoints && user.painPoints.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Pain Points</h4>
                <div className="flex flex-wrap gap-2">
                  {user.painPoints.map((point, index) => (
                    <span key={index} className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/20 dark:ring-red-400/20">
                      {point}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Basic User Information */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">Basic Information</h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user.displayName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Registration Date</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100"><SafeRender value={user.createdAt} /></dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100"><SafeRender value={user.lastLogin} /></dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">User ID</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-all">{user.id}</dd>
            </div>
            {user.submissionId && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Waitlist Submission ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-all">{user.submissionId}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Profile Information */}
      {(user.hasUserProfile || user.basicInfo) && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">Profile Details</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              {(user.basicInfo?.firstName || user.firstName) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">First Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {user.basicInfo?.firstName || user.firstName}
                  </dd>
                </div>
              )}
              {(user.basicInfo?.lastName || user.lastName) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {user.basicInfo?.lastName || user.lastName}
                  </dd>
                </div>
              )}
              {(user.basicInfo?.linkedinUrl || user.linkedinUrl) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">LinkedIn URL</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    <a 
                      href={user.basicInfo?.linkedinUrl || user.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 truncate block"
                    >
                      {user.basicInfo?.linkedinUrl || user.linkedinUrl}
                    </a>
                  </dd>
                </div>
              )}
            </dl>

            {/* Waitlist Data / Quiz Answers */}
            {((user.preferences && Object.keys(user.preferences).length > 0) || 
              (user.waitlistData?.quizAnswers && Object.keys(user.waitlistData.quizAnswers).length > 0)) && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Quiz Answers</h4>
                <div className="bg-white dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-700 text-sm max-h-60 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-xs text-gray-900 dark:text-gray-100">
                    {JSON.stringify(user.preferences || user.waitlistData?.quizAnswers, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Resume */}
            {(user.resumeUrl || user.resume?.resumeUrl) && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Resume</h4>
                <a 
                  href={user.resumeUrl || user.resume?.resumeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  {user.resumeFilename || user.resume?.filename || 'View Resume'}
                </a>
              </div>
            )}

            {/* Video Submissions */}
            {(user.videoSubmission?.videoUrl || user.videoResponses) && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Video Submission</h4>
                {user.videoSubmission?.videoUrl && (
                  <a 
                    href={user.videoSubmission.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    View Video Submission
                  </a>
                )}
                {user.videoResponses && Object.entries(user.videoResponses).length > 0 && (
                  <div className="mt-2 space-y-2">
                    {Object.entries(user.videoResponses).map(([id, response]) => (
                      <div key={id} className="pl-2 border-l-2 border-indigo-200 dark:border-indigo-700">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mb-1">
                          {response.question || id}
                        </p>
                        {response.videoUrl && (
                          <a 
                            href={response.videoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 flex items-center text-xs"
                          >
                            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Watch Video
                          </a>
                        )}
                        {response.transcript && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            <p className="font-medium">Transcript:</p>
                            <p className="italic text-gray-500 dark:text-gray-400 text-xs line-clamp-2">
                              {response.transcript.substring(0, 100)}...
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Waitlist Data (if not already shown) */}
      {user.waitlistData && !user.preferences && !user.profileSummary && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">Waitlist Information</h3>

            {user.waitlistData.submittedAt && (
              <dl className="grid grid-cols-1 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Submitted At</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    <SafeRender value={user.waitlistData.submittedAt} />
                  </dd>
                </div>
              </dl>
            )}

            {user.waitlistData.quizAnswers && Object.keys(user.waitlistData.quizAnswers).length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Quiz Answers</h4>
                <div className="bg-white dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-700 text-sm max-h-60 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-xs text-gray-900 dark:text-gray-100">{JSON.stringify(user.waitlistData.quizAnswers, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Access Control - Moved to bottom */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-medium leading-6 text-gray-900 dark:text-gray-100 mb-3">User Access Control</h3>
          <div className="space-y-3">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Control user access level. Users with "invited" or "active" status can complete onboarding and access the full application.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {['waitlist', 'invited', 'active'].map((status) => {
                const isActive = currentStatus === status;
                const statusConfig = {
                  'waitlist': { label: 'Waitlist', color: 'bg-yellow-500 hover:bg-yellow-600 ring-yellow-600' },
                  'invited': { label: 'Invited', color: 'bg-blue-500 hover:bg-blue-600 ring-blue-600' },
                  'active': { label: 'Active', color: 'bg-green-500 hover:bg-green-600 ring-green-600' }
                };
                const config = statusConfig[status];
                
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={isUpdatingStatus || isActive}
                    className={`
                      px-3 py-1.5 rounded-md text-xs font-medium text-white ring-2 ring-inset
                      transition-all duration-200
                      ${isActive 
                        ? `${config.color} cursor-default` 
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 ring-gray-400'
                      }
                      ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {isActive && '✓ '}
                    {config.label}
                  </button>
                );
              })}
            </div>
            {isUpdatingStatus && (
              <p className="text-xs text-gray-600 dark:text-gray-400 italic">Updating status...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 